<template>
  <b-card
    header-class="p-0"
    class="page-card"
    :class="{ 'token-invalid': tokenInvalid }"
    no-body
  >
    <div
      v-if="tokenInvalid"
      class="token-renew-overlay"
      role="button"
      tabindex="0"
      @click="renewToken"
      @keydown.enter="renewToken"
    >
      <div class="token-renew-content">
        <p class="mb-50 font-weight-bold">
          {{ $t('dashboard.token_needs_renew') }}
        </p>
        <span class="token-renew-link">{{ $t('dashboard.token_renew_click') }}</span>
      </div>
    </div>
    <template #header>
      <div
        class="mb-0 image-container"
        :style="{backgroundImage:`url(${require('@/assets/images/insta-cover.jpg')})`,height:'150px',width:'100%'}"
      >
        <div
          class="ribbon ribbon-small"
          :class="account.expired?'ribbon-red':'ribbon-success'"
        >
          <div class="banner">
            <div class="text">
              {{ getPageTrans(account.expired?'expired':'active') }}
            </div>
          </div>
        </div>
        <div class="page-cover-overlay" />
        <div class="page-name">
          <a
            class="h3"
            :href="`https://instagram.com/${account.account_id}`"
            target="_blank"
          >{{ account.name }}</a>
          <h6 class="mb-0 mt-1">
            {{ $t('page.'+(account.expired?'expired-at':'should_renew_at'),{date:account.expires_at}) }}
          </h6>
        </div>
        <div
          v-if="!account.is_direct"
          class="profile-image-wrapper"
        >
          <div class="profile-image p-0">
            <b-avatar
              size="90"
              variant="primary"
              :src="account.page.profile"
            />
          </div>
        </div>
      </div>
    </template>
    <b-card-body>
      <ul class="page-features">
        <feature-row
          :enabled="!account.expired"
          :page-id="account.id"
          title="reply.menu-title"
          route="owner:posts"
        >
          <b-button
            size="sm"
            variant="outline-primary"
            @click="openPage('owner:instagram-posts')"
          >
            {{ $t('reply.enable') }}
          </b-button>
        </feature-row>
        <feature-row
          :enabled="!account.expired"
          :page-id="account.id"
          title="menu.posting_service"
          route="owner:poster"
        >
          <b-button
            size="sm"
            variant="outline-primary"
            @click="openPage('owner:insta-poster')"
          >
            {{ $t('poster.post') }}
          </b-button>
        </feature-row>
        <feature-row
          :enabled="account.has_bot"
          :page-id="account.id"
          title="bot.title"
        >
          <b-button
            size="sm"
            :disabled="submitting"
            :variant="`${(currentAccount.bot_enabled)?'outline-danger':'outline-primary'}`"
            @click="enableDisableBot()"
          >
            <b-spinner
              v-if="submitting"
              small
              class="mr-50"
            />
            {{ $t(`bot.${(currentAccount.bot_enabled)?'disable-bot':'enable-bot'}`) }}
          </b-button>
        </feature-row>
      </ul>
    </b-card-body>
    <b-card-footer>
      <div class="d-flex justify-content-center text-dark flex-wrap gap-2">
        <div v-if="account.package">
          <span style="font-weight: 600">{{ getPageTrans('package-name') }}</span>
          {{ account.package.name }}
        </div>
        <b-button
          size="sm"
          variant="outline-danger"
          @click="deletePage"
        >
          <FeatherIcon
            icon="TrashIcon"
          />
          {{ $t('page.delete') }}
        </b-button>
      </div>
    </b-card-footer>
    <!--/ follower projects rank -->
  </b-card>
</template>

<script>
import {
  BCard, BAvatar, BCardFooter, BCardBody, BButton,
} from 'bootstrap-vue'
import FeatureRow from '@/views/owner-fb/page/components/FeatureRow'
import InstagramAccountsApi from '@/api/instagram/account'
import ChatBotApi from '@/api/bot'
import AuthMixin from '@/views/auth/mixins/auth-mixin'

export default {
  name: 'InstagramAccountCard',
  components: {
    FeatureRow,
    BCard,
    BAvatar,
    BButton,
    BCardFooter,
    BCardBody,
  },
  mixins: [AuthMixin],
  props: {
    account: {
      type: Object,
      default: () => {
      },
    },
    tokenInvalid: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      submitting: false,
      currentAccount: {},
    }
  },
  mounted() {
    this.currentAccount = { ...this.account }
  },
  methods: {
    renewToken() {
      if (this.account.is_direct) {
        this.authInstagramDirect(false)
      } else {
        this.authInstagram(false)
      }
    },
    async openPage(route) {
      await this.$store.dispatch('instagram/SET_ACCOUNT', this.account.id)
      await this.$store.commit('auth/SET_MODEL_TYPE', 'instagram')
      this.$router.push({ name: route })
    },
    getPageTrans(str) {
      return this.$t(`page.${str}`)
    },
    deletePage() {
      this.$okConfirm(this.$t('general.delete-confirm'), {
        preConfirm: () => InstagramAccountsApi.deleteAccountByOwner(this.account.id)
          .then(response => {
            const accounts = this.$store.getters['instagram/accounts'] ?? []
            const pageIndex = accounts.findIndex(account => account.id === this.account.id)
            if (pageIndex > -1) {
              accounts.splice(pageIndex, 1)
              this.$store.commit('instagram/SET_INSTA_ACCOUNTS', accounts)
            }
            this.$notifySuccess(response.message)
          })
          .catch(e => this.$notifyError(e)),
      })
    },
    async enableDisableBot() {
      const label = this.currentAccount.bot_enabled ? 'disable-bot-msg' : 'enable-bot-msg'
      this.$okConfirm(this.$t(`bot.${label}`), {
        confirmButtonText: this.$t('forms.submit'),
        showCancel: true,
        preConfirm: () => {
          // For direct Instagram accounts, page is null, so use account_id or 0 as pageId
          const pageId = this.account.page?.id ?? this.account.id ?? 0
          return ChatBotApi.enableDisableInstagramBot(pageId, this.account.id).then(res => {
            if (res.error === 0) {
              this.currentAccount.bot_enabled = !this.currentAccount.bot_enabled
              this.$store.dispatch('instagram/UPDATE_ACCOUNT', this.currentAccount)
              this.$notifySuccess(res.message)
            } else {
              this.$notifyError(res.message)
            }
          }).catch(e => this.$notifyError(e))
            .finally(() => {
              this.submitting = false
            })
        },
      })
    },
  },
}
</script>
